import React, { useMemo } from "react";
import { Select, Tag, Tooltip, Alert } from "antd";
import { InfoCircleOutlined, CheckCircleFilled } from "@ant-design/icons";
import {
  MappingRow,
  TemplateField,
} from "../../../../../api/smartMapper/smartMapperApis";

interface Props {
  template: { template_name: string; fields: TemplateField[] };
  mapping: MappingRow[];
  unmappedTarget: string[];
  onChange: (next: MappingRow[]) => void;
}

const confidenceTag = (c: number) => {
  if (c >= 0.85)
    return { color: "#16a34a", bg: "#f0fdf4", border: "#bbf7d0", label: `${Math.round(c * 100)}%` };
  if (c >= 0.6)
    return { color: "#d97706", bg: "#fffbeb", border: "#fde68a", label: `${Math.round(c * 100)}%` };
  if (c > 0)
    return { color: "#dc2626", bg: "#fef2f2", border: "#fecaca", label: `${Math.round(c * 100)}%` };
  return { color: "#64748b", bg: "#f1f5f9", border: "#cbd5e1", label: "ignored" };
};

const MappingReviewTable: React.FC<Props> = ({
  template,
  mapping,
  unmappedTarget,
  onChange,
}) => {
  // Which target field names are already used → disable them in other rows'
  // dropdowns to prevent two sources claiming the same target.
  const usedTargets = useMemo(() => {
    const set = new Set<string>();
    mapping.forEach((m) => {
      if (m.target) set.add(m.target);
    });
    return set;
  }, [mapping]);

  const handleTargetChange = (rowIndex: number, target: string | null) => {
    const next = mapping.map((m, i) =>
      i === rowIndex
        ? {
            ...m,
            target,
            // user-confirmed selection → max confidence
            confidence: target ? 1.0 : 0.0,
            is_master_data:
              template.fields.find((f) => f.name === target)?.is_master_data ??
              m.is_master_data,
          }
        : m
    );
    onChange(next);
  };

  const targetOptions = template.fields.map((f) => ({
    value: f.name,
    label: f.label,
    isUsed: usedTargets.has(f.name),
    field: f,
  }));

  return (
    <div>
      {unmappedTarget.length > 0 && (
        <Alert
          type="info"
          showIcon
          icon={<InfoCircleOutlined />}
          message="Unassigned template fields"
          description={
            <div style={{ marginTop: 4 }}>
              {unmappedTarget.map((t) => {
                const f = template.fields.find((x) => x.name === t);
                return (
                  <Tag
                    key={t}
                    style={{
                      fontSize: 11,
                      borderRadius: 999,
                      padding: "2px 10px",
                      marginBottom: 4,
                    }}
                  >
                    {f?.label || t}
                    {f?.required && (
                      <span style={{ color: "#dc2626", marginLeft: 4 }}>*</span>
                    )}
                  </Tag>
                );
              })}
              <div style={{ marginTop: 6, fontSize: 12, color: "#64748b" }}>
                No source column is mapped to these. Required fields will cause
                errors during transform.
              </div>
            </div>
          }
          style={{ marginBottom: 14, borderRadius: 8 }}
        />
      )}

      <div
        style={{
          border: "1px solid #e6e8ef",
          borderRadius: 10,
          overflow: "hidden",
        }}
      >
        <div
          style={{
            display: "grid",
            // minmax(0, 1fr) makes each fr column share the row evenly
            // regardless of content width — otherwise rows with longer
            // comments stretch the first column and throw the dropdowns
            // out of horizontal alignment with the rest of the table.
            gridTemplateColumns: "minmax(0, 1.2fr) 28px minmax(0, 1.4fr) 110px",
            gap: 12,
            padding: "10px 14px",
            background: "#f8fafc",
            borderBottom: "1px solid #e6e8ef",
            fontSize: 11.5,
            fontWeight: 600,
            color: "#475569",
            textTransform: "uppercase",
            letterSpacing: 0.4,
          }}
        >
          <div style={{ minWidth: 0 }}>Source column</div>
          <div></div>
          <div style={{ minWidth: 0 }}>Target field</div>
          <div style={{ textAlign: "right", minWidth: 0 }}>Confidence</div>
        </div>

        {mapping.map((row, idx) => {
          const cTag = confidenceTag(row.confidence);
          return (
            <div
              key={`${row.source}-${idx}`}
              style={{
                display: "grid",
                gridTemplateColumns:
                  "minmax(0, 1.2fr) 28px minmax(0, 1.4fr) 110px",
                gap: 12,
                padding: "12px 14px",
                borderBottom:
                  idx === mapping.length - 1 ? "none" : "1px solid #eef0f3",
                alignItems: "center",
                background: idx % 2 === 0 ? "#fff" : "#fafbfc",
              }}
            >
              <div style={{ minWidth: 0 }}>
                <div
                  style={{
                    fontSize: 13.5,
                    fontWeight: 600,
                    color: "#0f172a",
                    wordBreak: "break-word",
                  }}
                >
                  {row.source}
                </div>
                {row.comments && (
                  <Tooltip
                    title={row.comments}
                    getPopupContainer={(triggerNode) =>
                      (triggerNode.parentElement as HTMLElement) || document.body
                    }
                  >
                    <div
                      style={{
                        fontSize: 11.5,
                        color: "#64748b",
                        marginTop: 2,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {row.comments}
                    </div>
                  </Tooltip>
                )}
              </div>

              <div style={{ color: "#94a3b8", textAlign: "center", fontSize: 16 }}>
                →
              </div>

              <Select
                value={row.target ?? "__ignore__"}
                onChange={(v) =>
                  handleTargetChange(idx, v === "__ignore__" ? null : v)
                }
                style={{ width: "100%", minWidth: 0 }}
                size="middle"
                // Anchor the popup to the trigger's parent so it inherits
                // the fullscreen overlay's z-index stack — otherwise the
                // default `document.body` portal sits at z-index ~1050,
                // behind our overlay (9999), and the dropdown disappears.
                getPopupContainer={(triggerNode) =>
                  (triggerNode.parentElement as HTMLElement) || document.body
                }
                options={[
                  {
                    value: "__ignore__",
                    label: (
                      <span style={{ color: "#94a3b8" }}>
                        Ignore this column
                      </span>
                    ),
                  },
                  ...targetOptions.map((o) => ({
                    value: o.value,
                    disabled: o.isUsed && o.value !== row.target,
                    label: (
                      <span
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 6,
                        }}
                      >
                        <span>{o.label}</span>
                        {o.field.required && (
                          <span style={{ color: "#dc2626" }}>*</span>
                        )}
                        {o.field.is_master_data && (
                          <Tag
                            color="purple"
                            style={{
                              marginLeft: 4,
                              fontSize: 10,
                              borderRadius: 4,
                              padding: "0 6px",
                            }}
                          >
                            master
                          </Tag>
                        )}
                      </span>
                    ),
                  })),
                ]}
              />

              <div
                style={{
                  textAlign: "right",
                  minWidth: 0,
                  display: "flex",
                  justifyContent: "flex-end",
                }}
              >
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 4,
                    background: cTag.bg,
                    border: `1px solid ${cTag.border}`,
                    color: cTag.color,
                    padding: "2px 10px",
                    borderRadius: 999,
                    fontSize: 11,
                    fontWeight: 600,
                    whiteSpace: "nowrap",
                  }}
                >
                  {row.confidence >= 1.0 && (
                    <CheckCircleFilled style={{ fontSize: 10 }} />
                  )}
                  {cTag.label}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default MappingReviewTable;
